import React, { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { setLoginCookie, getSelf } from '@vlabs/api/artemy/client';
import type { LoginFormValues } from '@vlabs/monokit/entities/auth';
import { LoginForm } from '@vlabs/monokit/entities/auth';
import { Page, PageContent } from '@vlabs/monokit/shared/ui/page';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useLoginPageTranslation } from './i18n';
import st from './LoginPage.module.sass';

interface LoginPageProps {
  CustomBrand?: React.ReactNode;
}

function LoginPage({
  CustomBrand = undefined,
}: LoginPageProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const history = useHistory();
  const { t } = useLoginPageTranslation();
  const [error, setError] = useState<string>();
  const [isAnimationOn, setIsAnimationOn] = useState(false);

  useEffect(() => {
    const animationTimer = setTimeout(() => setIsAnimationOn(false), 1000);
    return () => {
      clearTimeout(animationTimer);
    };
  }, [isAnimationOn]);

  const onSubmit: (values: LoginFormValues) => void = async ({ login, password }) => {
    setError(undefined);
    try {
      await setLoginCookie({ auth: { username: login, password } });
      const account = await getSelf();
      queryClient.setQueryData(['self'], account);
      history.push('/');
    } catch (err) {
      const { status } = err as { status: number };
      if (status === 401) {
        setError(t('error.login_or_password_is_wrong'));
      } else if (status === 403) {
        setError(t('error.user_is_blocked'));
      }
    }
  };

  return (
    <Page>
      <PageContent className={st.Root}>
        {!!CustomBrand && <div className={st.Brand}>{CustomBrand}</div>}
        <div className={classNames(st.Wrapper, { [st.Error]: error })}>
          <LoginForm
            error={error}
            onSubmit={onSubmit}
          />
        </div>
      </PageContent>
    </Page>
  );
}

export { LoginPage };
